import { useState, useEffect, type FormEvent } from 'react';
import { ApiError } from '../api/client';
import { can } from '../auth/store';
import { useCreatePerson, useAssignPersonRole } from '../api/queries/people';
import { useCreateUser, useRbacRoles } from '../api/queries/settings';
import { useLookups, activeOptionsFor } from '../api/queries/lookups';
import { PhoneListInput, type PhoneEntry } from './PhoneListInput';
import { EmailListInput, emailsToPayload, type EmailEntry } from './EmailListInput';
import { AddressListInput, addressesToPayload, type AddressEntry } from './AddressListInput';
import { stripPhoneForStorage } from '../utils/phone';
import { RolePills } from './RolePills';
import { OrgPicker } from './OrgPicker';
import { Modal } from './Modal';
import type { RoleBadge } from '@stfors/shared-types';
import '../pages/NewClientPage.css';

interface AddIndividualModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * Called with the new person id after a successful create. The name is the
   * server's generated `display_name` (honorifics dropped, suffix folded in) —
   * callers that show it must not re-derive it from the form fields.
   */
  onCreated?: (personId: number, displayName: string) => void;
  /** Override modal title (e.g. company contact flow). */
  title?: string;
  /** Primary submit button label. */
  submitLabel?: string;
  /**
   * When set (company "Add contact → new individual"), Contact is pre-selected
   * for this org and the company picker is hidden — the person is linked on create.
   */
  presetContactOrgId?: number;
}

/**
 * Entity-first "Add Individual": create the person once, then tag it with any
 * of Client / Contractor / Vendor / Contact (each becomes a minimal role or
 * org affiliation). An email is required for Contractor. Contact requires a
 * company (or presetContactOrgId from the company page).
 */
export function AddIndividualModal({
  open,
  onClose,
  onCreated,
  title: modalTitle = 'Add individual',
  submitLabel = 'Create individual',
  presetContactOrgId,
}: AddIndividualModalProps): JSX.Element {
  const createPerson = useCreatePerson();
  const assignRole = useAssignPersonRole();
  const createUser = useCreateUser();
  const canManageUsers = can('settings.manage_users');
  const rbacRoles = useRbacRoles();
  const roleOptions = rbacRoles.data?.roles ?? [];
  const lookups = useLookups();
  const titleOptions = activeOptionsFor(lookups.data?.lookup_options, 'title');
  const suffixOptions = activeOptionsFor(lookups.data?.lookup_options, 'suffix');
  const phoneTypeOptions = activeOptionsFor(lookups.data?.lookup_options, 'phone_type');
  const emailTypeOptions = activeOptionsFor(lookups.data?.lookup_options, 'email_type');
  const addressTypeOptions = activeOptionsFor(lookups.data?.lookup_options, 'address_type');

  const lockedToCompany = presetContactOrgId !== undefined;

  const [title, setTitle] = useState('');
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [suffix, setSuffix] = useState('');
  const [phones, setPhones] = useState<PhoneEntry[]>([{ phone_type: 'Mobile', phone: '' }]);
  const [emails, setEmails] = useState<EmailEntry[]>([]);
  const [addresses, setAddresses] = useState<AddressEntry[]>([]);
  const [notes, setNotes] = useState('');
  const [roles, setRoles] = useState<RoleBadge[]>(
    lockedToCompany ? ['contact'] : [],
  );
  /** Company required when Contact role is selected (organization_contacts). */
  const [contactOrgId, setContactOrgId] = useState<number | null>(
    presetContactOrgId ?? null,
  );
  const [error, setError] = useState<string | null>(null);

  // Re-seed contact role when opening in company-locked mode (modal reuses state).
  useEffect(() => {
    if (!open || presetContactOrgId === undefined) return;
    setRoles((prev) => (prev.includes('contact') ? prev : [...prev, 'contact']));
    setContactOrgId(presetContactOrgId);
  }, [open, presetContactOrgId]);

  // Staff (login account) — only offered to settings.manage_users holders.
  const [makeStaff, setMakeStaff] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [staffRoleNames, setStaffRoleNames] = useState<string[]>([]);

  const busy = createPerson.isPending || assignRole.isPending || createUser.isPending;

  const reset = () => {
    setTitle(''); setFirst(''); setLast(''); setSuffix('');
    setPhones([{ phone_type: 'Mobile', phone: '' }]);
    setEmails([]); setAddresses([]); setNotes('');
    setRoles(lockedToCompany ? ['contact'] : []);
    setContactOrgId(presetContactOrgId ?? null);
    setError(null);
    setMakeStaff(false); setLoginEmail(''); setLoginPassword(''); setStaffRoleNames([]);
  };

  const toggleStaff = (next: boolean) => {
    setMakeStaff(next);
    // Prefill the login email with the person's primary email for convenience.
    if (next && loginEmail.trim().length === 0) {
      const primary = emailsToPayload(emails)[0]?.email;
      if (primary) setLoginEmail(primary);
    }
  };
  const toggleStaffRole = (name: string) =>
    setStaffRoleNames((prev) => (prev.includes(name) ? prev.filter((r) => r !== name) : [...prev, name]));
  const close = () => {
    if (busy) return;
    reset();
    onClose();
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (first.trim().length === 0 && last.trim().length === 0) {
      setError('A first or last name is required.');
      return;
    }

    const emailsPayload = emailsToPayload(emails);
    const addressesPayload = addressesToPayload(addresses);
    // The person's primary email doubles as the role (billing) email when
    // tagging Client/Contractor; Contractor requires one.
    const primaryEmail = emailsPayload[0]?.email;
    if (roles.includes('contractor') && !primaryEmail) {
      setError('An email is required to tag this person as a Contractor — add one under Emails.');
      return;
    }
    if (roles.includes('contact') && contactOrgId === null) {
      setError('Pick a company to tag this person as a Contact.');
      return;
    }
    if (makeStaff) {
      if (loginEmail.trim().length === 0) { setError('A login email is required for a Staff account.'); return; }
      if (loginPassword.length < 12) { setError('Staff password must be at least 12 characters.'); return; }
      if (staffRoleNames.length === 0) { setError('Pick at least one access role for the Staff account.'); return; }
    }

    const phonesPayload = phones
      .map((p) => ({ phone_type: p.phone_type, phone: stripPhoneForStorage(p.phone) ?? '' }))
      .filter((p) => p.phone.length === 10 && p.phone_type.length > 0);

    try {
      const res = await createPerson.mutateAsync({
        title: title.trim() || undefined,
        first_name: first.trim() || undefined,
        last_name: last.trim() || undefined,
        suffix: suffix.trim() || undefined,
        notes: notes.trim() || undefined,
        phones: phonesPayload.length > 0 ? phonesPayload : undefined,
        emails: emailsPayload.length > 0 ? emailsPayload : undefined,
        addresses: addressesPayload.length > 0 ? addressesPayload : undefined,
      });
      const personId = res.person.id;

      try {
        for (const role of roles) {
          await assignRole.mutateAsync({
            personId,
            role,
            email: role === 'client' || role === 'contractor' ? primaryEmail : undefined,
            organization_id: role === 'contact' ? contactOrgId! : undefined,
          });
        }
        if (makeStaff) {
          await createUser.mutateAsync({
            person_id: personId,
            email: loginEmail.trim(),
            password: loginPassword,
            display_name: res.person.display_name,
            role_names: staffRoleNames,
          });
        }
      } catch (roleErr) {
        const m = roleErr instanceof ApiError ? roleErr.message : 'a role could not be added';
        setError(`Person created, but ${m}. Finish from their page.`);
        onCreated?.(personId, res.person.display_name);
        return;
      }

      reset();
      onCreated?.(personId, res.person.display_name);
      onClose();
    } catch (err) {
      if (err instanceof ApiError && (err.status === 409 || err.status === 400)) {
        setError(err.message);
        return;
      }
      setError('Could not create the individual. Try again.');
    }
  };

  return (
    <Modal
      title={modalTitle}
      open={open}
      onClose={close}
      busy={busy}
      footer={
        <>
          <button type="button" className="btn-charcoal-secondary" onClick={close} disabled={busy}>
            Cancel
          </button>
          <button
            type="submit"
            form="add-individual-form"
            className="btn-cyan-primary"
            disabled={busy || (first.trim().length === 0 && last.trim().length === 0)}
          >
            {busy ? 'Creating…' : submitLabel}
          </button>
        </>
      }
    >
      <form id="add-individual-form" className="new-client-form" onSubmit={onSubmit} noValidate style={{ padding: 0 }}>
        <div className="new-client-row">
          <label className="new-client-field new-client-field-narrow">
            <span>Title</span>
            <select className="form-input" value={title} onChange={(e) => setTitle(e.target.value)}>
              <option value="">—</option>
              {titleOptions.map((o) => <option key={o.id} value={o.value}>{o.value}</option>)}
            </select>
          </label>
          <label className="new-client-field">
            <span>First name</span>
            <input className="form-input" value={first} onChange={(e) => setFirst(e.target.value)} maxLength={100} />
          </label>
          <label className="new-client-field">
            <span>Last name</span>
            <input className="form-input" value={last} onChange={(e) => setLast(e.target.value)} maxLength={100} />
          </label>
          <label className="new-client-field new-client-field-narrow">
            <span>Suffix</span>
            <select className="form-input" value={suffix} onChange={(e) => setSuffix(e.target.value)}>
              <option value="">—</option>
              {suffixOptions.map((o) => <option key={o.id} value={o.value}>{o.value}</option>)}
            </select>
          </label>
        </div>

        <div className="new-client-field new-client-field-full">
          <span>Phone numbers</span>
          <PhoneListInput value={phones} onChange={setPhones} typeOptions={phoneTypeOptions} disabled={busy} />
        </div>

        <div className="new-client-field new-client-field-full">
          <span>Emails</span>
          <EmailListInput value={emails} onChange={setEmails} typeOptions={emailTypeOptions} disabled={busy} />
        </div>

        <div className="new-client-field new-client-field-full">
          <span>Addresses</span>
          <AddressListInput value={addresses} onChange={setAddresses} typeOptions={addressTypeOptions} disabled={busy} />
        </div>

        <label className="new-client-field new-client-field-full">
          <span>Notes</span>
          <textarea className="form-input new-client-textarea" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} maxLength={10_000} />
        </label>

        <div className="new-client-field new-client-field-full">
          <span>Roles (optional)</span>
          <RolePills
            value={roles}
            onChange={(next) => {
              // Company-locked flow always keeps Contact selected.
              if (lockedToCompany && !next.includes('contact')) {
                setRoles([...next, 'contact']);
                return;
              }
              setRoles(next);
            }}
            disabled={busy}
            showStaff={canManageUsers && !lockedToCompany}
            staffSelected={makeStaff}
            onStaffToggle={toggleStaff}
          />
          {roles.includes('contractor') && (
            <p className="field-error" style={{ color: 'var(--color-silver-metallic)' }}>
              Tagging as Contractor uses the primary email above (required).
            </p>
          )}
          {roles.includes('contact') && !lockedToCompany && (
            <div className="new-client-field new-client-field-full" style={{ marginTop: 'var(--space-3)' }}>
              <span className="new-client-section-label">Company for Contact role *</span>
              <OrgPicker value={contactOrgId} onChange={(oid) => setContactOrgId(oid)} disabled={busy} />
            </div>
          )}
          {lockedToCompany && (
            <p className="field-error" style={{ color: 'var(--color-silver-metallic)', marginTop: 'var(--space-2)' }}>
              This person will be linked as a Contact on this company.
            </p>
          )}
        </div>

        {makeStaff && canManageUsers && (
          <>
            <div className="new-client-row">
              <label className="new-client-field">
                <span>Login email *</span>
                <input className="form-input" type="email" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} maxLength={254} />
              </label>
              <label className="new-client-field">
                <span>Initial password * (≥12)</span>
                <input className="form-input" type="text" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} minLength={12} />
              </label>
            </div>
            <div className="new-client-field new-client-field-full">
              <span>Access roles *</span>
              <div className="role-pills" role="group" aria-label="Access roles">
                {roleOptions.map((r) => {
                  const selected = staffRoleNames.includes(r.name);
                  return (
                    <button
                      key={r.id}
                      type="button"
                      className={`role-pill role-pill-generic${selected ? ' role-pill-selected' : ''}`}
                      aria-pressed={selected}
                      onClick={() => toggleStaffRole(r.name)}
                      disabled={busy}
                      title={r.description ?? undefined}
                    >
                      {r.name}
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        )}

        {error && <p className="new-client-error" role="alert">{error}</p>}
      </form>
    </Modal>
  );
}
